import { savedTrips } from "../../src/saved-trips-store";
import { TRAVEL_MODE_ICON, type TravelMode, type TripState } from "../../src/types";
import { buildDayTimeline, tripStats, type DayCell } from "./compare";

const MAX_SELECTED = 4;
const MODE_LABEL: Record<TravelMode, string> = {
  plane: "Avión",
  train: "Tren",
  bus: "Autobús",
  car: "Coche",
};
/** Fixed accent palette; column i uses ACCENTS[i]. */
const ACCENTS = ["#4f46e5", "#0d9488", "#d97706", "#e11d48"];

const els = {
  selector: required<HTMLDivElement>("#tc-selector"),
  selectorHint: required<HTMLParagraphElement>("#tc-selector-hint"),
  empty: required<HTMLParagraphElement>("#tc-empty"),
  overviewSection: required<HTMLElement>("#tc-overview-section"),
  overview: required<HTMLTableElement>("#tc-overview"),
  daybydaySection: required<HTMLElement>("#tc-daybyday-section"),
  daybyday: required<HTMLTableElement>("#tc-daybyday"),
  daybydayNote: required<HTMLParagraphElement>("#tc-daybyday-note"),
};

let trips: readonly TripState[] = [];
const selected: string[] = [];

function start(): void {
  savedTrips.load();
  trips = savedTrips.list();
  // Preselect the first two so something renders immediately.
  for (const trip of trips.slice(0, 2)) selected.push(trip.id);
  renderSelector();
  renderComparison();
}

function accentFor(tripId: string): string {
  const idx = selected.indexOf(tripId);
  return idx === -1 ? "var(--border-strong)" : ACCENTS[idx % ACCENTS.length];
}

function toggle(tripId: string): void {
  const idx = selected.indexOf(tripId);
  if (idx !== -1) {
    selected.splice(idx, 1);
  } else if (selected.length < MAX_SELECTED) {
    selected.push(tripId);
  }
  renderSelector();
  renderComparison();
}

function renderSelector(): void {
  els.selector.replaceChildren();

  if (trips.length === 0) {
    els.selectorHint.textContent =
      "No hay viajes guardados todavía. Creá viajes en Trip Planner y volvé.";
    return;
  }

  const dateFmt = new Intl.DateTimeFormat("es", { day: "numeric", month: "short", year: "numeric" });
  const atLimit = selected.length >= MAX_SELECTED;

  for (const trip of trips) {
    const isSelected = selected.includes(trip.id);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tc-chip";
    if (isSelected) btn.classList.add("is-selected");
    btn.disabled = !isSelected && atLimit;
    if (isSelected) btn.style.setProperty("--accent", accentFor(trip.id));

    const name = document.createElement("span");
    name.className = "tc-chip-name";
    const dot = document.createElement("span");
    dot.className = "tc-chip-dot";
    const nameText = document.createElement("span");
    nameText.textContent = trip.tripName?.trim() || "(sin nombre)";
    name.append(dot, nameText);

    const meta = document.createElement("span");
    meta.className = "tc-chip-meta";
    meta.textContent = `${formatDate(trip.createdAt, dateFmt)} · ${stopCountLabel(trip.stops.length)}`;

    btn.append(name, meta);
    btn.addEventListener("click", () => toggle(trip.id));
    els.selector.appendChild(btn);
  }

  els.selectorHint.textContent = `${selected.length}/${MAX_SELECTED} seleccionados · mínimo 2 para comparar.`;
}

function renderComparison(): void {
  const chosen = trips.filter((t) => selected.includes(t.id));
  // Keep column order matching selection order (and thus the accent palette).
  chosen.sort((a, b) => selected.indexOf(a.id) - selected.indexOf(b.id));

  const enough = chosen.length >= 2;
  els.overviewSection.hidden = !enough;
  els.daybydaySection.hidden = !enough;
  els.empty.hidden = enough;

  if (!enough) {
    els.empty.textContent =
      trips.length === 0
        ? "No hay viajes para comparar."
        : "Seleccioná al menos 2 viajes para ver la comparación.";
    return;
  }

  renderOverview(chosen);
  renderDayByDay(chosen);
}

function renderOverview(chosen: readonly TripState[]): void {
  const stats = chosen.map(tripStats);
  const dateFmt = new Intl.DateTimeFormat("es", { day: "numeric", month: "short", year: "numeric" });
  els.overview.replaceChildren();

  // Header row: metric label + one column per trip.
  const head = document.createElement("tr");
  head.appendChild(th("", "tc-metric"));
  chosen.forEach((trip, i) => {
    const cell = document.createElement("th");
    cell.className = "tc-trip-head";
    cell.style.setProperty("--accent", ACCENTS[i % ACCENTS.length]);
    const name = document.createElement("div");
    name.className = "tc-trip-name";
    name.textContent = trip.tripName?.trim() || "(sin nombre)";
    const date = document.createElement("div");
    date.className = "tc-trip-date";
    date.textContent = formatDate(trip.createdAt, dateFmt);
    cell.append(name, date);
    head.appendChild(cell);
  });
  els.overview.appendChild(head);

  addMetricRow("Duración", stats.map((s) => (s.durationDays == null ? "— días" : dayCountLabel(s.durationDays))));
  addMetricRow("Paradas", stats.map((s) => stopCountLabel(s.stopCount)));
  addMetricRow("Hoteles", stats.map((s) => String(s.hotelCount)));
  addMetricRow("Distancia total", stats.map((s) => `${s.totalKm.toLocaleString("es")} km`));
  addMetricRow("Transporte", stats.map((s) => modesText(s.modes)), "tc-modes");
  addMetricRow("Ciudades", stats.map((s) => (s.cities.length ? s.cities.join(" → ") : "—")), "tc-cities");
}

function addMetricRow(label: string, values: string[], valueClass?: string): void {
  const tr = document.createElement("tr");
  tr.appendChild(td(label, "tc-metric"));
  for (const value of values) tr.appendChild(td(value, valueClass));
  els.overview.appendChild(tr);
}

function renderDayByDay(chosen: readonly TripState[]): void {
  const timelines = chosen.map(buildDayTimeline);
  const maxDays = timelines.reduce((m, t) => Math.max(m, t.length), 0);
  const hasApprox = timelines.some((t) => t.some((c) => c.approx));

  els.daybydayNote.hidden = !hasApprox;
  if (hasApprox) {
    els.daybydayNote.textContent =
      "≈ Un viaje sin fechas se muestra por orden de paradas (una jornada por parada).";
  }

  els.daybyday.replaceChildren();

  const head = document.createElement("tr");
  head.appendChild(th("", "tc-day-label"));
  chosen.forEach((trip, i) => {
    const cell = document.createElement("th");
    cell.className = "tc-trip-head";
    cell.style.setProperty("--accent", ACCENTS[i % ACCENTS.length]);
    cell.textContent = trip.tripName?.trim() || "(sin nombre)";
    head.appendChild(cell);
  });
  els.daybyday.appendChild(head);

  for (let day = 0; day < maxDays; day++) {
    const tr = document.createElement("tr");
    tr.appendChild(td(`Día ${day + 1}`, "tc-day-label"));
    for (const timeline of timelines) {
      tr.appendChild(dayCell(timeline[day]));
    }
    els.daybyday.appendChild(tr);
  }
}

function dayCell(cell: DayCell | undefined): HTMLTableCellElement {
  const td = document.createElement("td");
  if (!cell) {
    const span = document.createElement("span");
    span.className = "tc-day-empty";
    span.textContent = "—";
    td.appendChild(span);
    return td;
  }

  const city = document.createElement("span");
  city.className = "tc-day-city";
  if (cell.isTravelDay) city.classList.add("is-travel");
  const icon = cell.isTravelDay && cell.arriveBy ? `${TRAVEL_MODE_ICON[cell.arriveBy]} ` : "";
  city.textContent = `${icon}${cell.city}`;
  td.appendChild(city);

  if (cell.placesCount > 0) {
    const places = document.createElement("span");
    places.className = "tc-day-places";
    places.textContent = cell.placesCount === 1 ? "1 lugar" : `${cell.placesCount} lugares`;
    td.appendChild(places);
  }
  return td;
}

function modesText(modes: TravelMode[]): string {
  if (modes.length === 0) return "—";
  return modes.map((m) => `${TRAVEL_MODE_ICON[m]}`).join(" ") + `  ${modes.map((m) => MODE_LABEL[m]).join(", ")}`;
}

function stopCountLabel(n: number): string {
  return n === 1 ? "1 parada" : `${n} paradas`;
}

function dayCountLabel(n: number): string {
  return n === 1 ? "1 día" : `${n} días`;
}

function formatDate(iso: string | undefined, fmt: Intl.DateTimeFormat): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : fmt.format(date);
}

function th(text: string, className?: string): HTMLTableCellElement {
  const cell = document.createElement("th");
  if (className) cell.className = className;
  cell.textContent = text;
  return cell;
}

function td(text: string, className?: string): HTMLTableCellElement {
  const cell = document.createElement("td");
  if (className) cell.className = className;
  cell.textContent = text;
  return cell;
}

function required<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Element not found: ${selector}`);
  return el;
}

start();
